import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast et gyldigt telefonnummer (mindst 8 tal)';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast en gyldig e-mailadresse';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse af opgaven skal udfyldes';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Beskrivelsen skal være på mindst 10 tegn';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'ok' || response.status === 200) {
          setStatus('success');
          setFormData({
            name: '',
            phone: '',
            email: '',
            description: '',
            website_url: '',
          });
        } else {
          throw new Error('Der opstod en fejl ved afsendelsen.');
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der opstod en fejl på serveren. Prøv igen senere.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Kunne ikke sende formularen. Kontroller din internetforbindelse.');
    }
  };

  return (
    <div className="bg-white p-6 sm:p-10 rounded-xl border border-slate-100 shadow-sm">
      <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
        Få et uforpligtende tilbud
      </h3>
      <p className="text-slate-500 text-sm mb-6">
        Udfyld formularen, så vender vi tilbage til dig inden for 24 timer med en professionel vurdering af din opgave.
      </p>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <div className="h-12 w-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa-solid fa-check text-xl" />
          </div>
          <h4 className="text-lg font-bold text-slate-900 mb-2">Tak for din henvendelse!</h4>
          <p className="text-slate-600 text-sm">
            Vi har modtaget dine oplysninger og kontakter dig hurtigst muligt for en uforpligtende snak om dit projekt.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 inline-flex items-center justify-center rounded bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold transition-colors"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Efterlad dette felt tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Navn <span className="text-emerald-600">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className={`w-full rounded border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                  : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-100'
              }`}
            />
            {errors.name && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.name}</p>}
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Telefonnummer <span className="text-emerald-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 60183562"
                className={`w-full rounded border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 ${
                  errors.phone
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-100'
                }`}
              />
              {errors.phone && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.phone}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                E-mail <span className="text-emerald-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className={`w-full rounded border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 ${
                  errors.email
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                    : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-100'
                }`}
              />
              {errors.email && <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.email}</p>}
            </div>
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-slate-700 mb-1.5">
              Beskrivelse af opgaven <span className="text-emerald-600">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Beskriv dit projekt... F.eks. opbygning af træterrasse på ca. 40 kvm, udskiftning af 3 vinduer osv."
              className={`w-full rounded border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 ${
                errors.description
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                  : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-100'
              }`}
            />
            {errors.description && (
              <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.description}</p>
            )}
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <i className="fa-solid fa-triangle-exclamation text-red-600 mt-0.5" />
              <p className="text-xs text-red-700 font-medium">{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 text-white py-3.5 text-sm font-semibold tracking-wide transition-all duration-200 hover:shadow-lg hover:shadow-emerald-900/10 active:scale-[0.98]"
          >
            {status === 'loading' ? (
              <>
                <i className="fa-solid fa-spinner-third animate-spin mr-2" />
                Sender oplysninger...
              </>
            ) : (
              <>
                Send forespørgsel
                <i className="fa-regular fa-arrow-right ml-2" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
